/**
 * ========================================
 * PARENT SERVICE
 * ========================================
 * Parent accounts: sign-up, linking a child, the read-only parent dashboard, and payment consent.
 * Also the child's side of it (link code, linked parents, "ask my parent" for a plan) and the silent learning-time
 * heartbeat.
 *
 * Endpoints: /api/v1/parents/register, /api/v1/parent/*, /api/v1/parent-link/*, /api/v1/learning-time/heartbeat
 *
 * NOTE: fields are raw snake_case, matching the backend JSON exactly (no auto-transform).
 *
 * Every figure here is calculated by the server. The dashboard never computes a metric and never calls the LLM:
 * `insight` is text the server stored earlier.
 */

import { apiClient } from './client';
import { handleAPIError } from './error-handler';

interface BackendEnvelope<T> {
  status: string;
  message: string;
  data: T;
}

export type Trend = 'up' | 'down' | 'flat' | null;
export type ParentPeriod = 'week' | 'month';

export interface ParentChild {
  id: string;
  first_name: string;
  name: string;
  standard: number | null;
  profile_image: string | null;
  overall_progress?: number | null;
}

export interface ParentInsight {
  id: string;
  date: string;
  triggers: string[];
  summary: string;
  going_well: string[];
  needs_attention: string[];
  recommendation: string;
  encouragement: string;
}

export interface ParentSubject {
  id: string;
  name: string;
  progress: number;
  completed: boolean;
  started: boolean;
  previous: number | null;
  change: number | null;
  trend: Trend;
}

export interface ParentSkill {
  id: string;
  name: string;
  module: string;
  subject: string;
  subject_id: string;
  score: number;
  status: 'strong' | 'developing' | 'needs_practice';
  attempts: number;
}

export interface ParentHome {
  child: ParentChild;
  period: ParentPeriod;
  overall: { value: number | null; change: number | null; trend: Trend; label: string };
  activity: { learning_seconds: number; lessons_completed: number; learning_days: number; days_total: number };
  subjects: ParentSubject[];
  strengths: string[];
  needs_attention: string[];
  insight: ParentInsight | null;
}

export interface ParentProgress {
  child: ParentChild;
  overall: { value: number | null; previous: number | null; change: number | null; label: string };
  subjects: (ParentSubject & {
    strong: ParentSkill[];
    developing: ParentSkill[];
    needs_practice: ParentSkill[];
    trend_points: { date: string; progress: number }[];
  })[];
}

export interface ParentAssessment {
  id: string;
  kind: 'quiz' | 'test';
  title: string;
  subject: string;
  score: number;
  correct: number | null;
  total: number | null;
  passed: boolean | null;
  taken_at: string | null;
  trend: Trend;
  breakdown: Record<string, unknown> | null;
}

export interface ParentAssessments {
  child: ParentChild;
  average: number | null;
  count: number;
  items: ParentAssessment[];
}

export interface ParentActivity {
  child: ParentChild;
  period: ParentPeriod;
  learning_seconds: number;
  lessons_completed: number;
  practice_sessions: number;
  assessments: number;
  learning_days: number;
  days_total: number;
  daily: { date: string; seconds: number; active: boolean }[];
}

export interface PlanRequest {
  id: string;
  child_user_id: string;
  status: 'pending' | 'approved' | 'paid' | 'declined' | 'cancelled';
  note: string | null;
  decline_reason: string | null;
  created_at: string | null;
  decided_at: string | null;
  plan: { id: string; name: string; price: number; currency: string; billing_cycle: string } | null;
}

export interface ParentChildPayments {
  child: ParentChild;
  subscription: { status: string; plan: { name: string; price: number; currency: string; billing_cycle: string }; next_billing_date?: string | null } | null;
  requests: PlanRequest[];
  payments: { items: { id: string; amount: number; currency: string; status: string; method?: string | null; created_at?: string | null }[]; total: number };
}

export interface ParentLinkStatus {
  code: { code: string; expires_at: string };
  parents: { id: string; name: string; linked_at: string | null }[];
  plan_request: PlanRequest | null;
}

export interface ParentRegisterPayload {
  first_name: string;
  last_name?: string;
  email: string;
  phone_number: string;
  password: string;
}

class ParentService {
  private base = '/api/v1/parent';

  private async get<T>(url: string, params?: Record<string, string>): Promise<T> {
    try {
      const response = await apiClient.get<BackendEnvelope<T>>(url, params ? { params } : undefined);
      return response.data.data;
    } catch (error) {
      handleAPIError(error);
    }
  }

  private async post<T>(url: string, body: unknown = {}): Promise<T> {
    try {
      const response = await apiClient.post<BackendEnvelope<T>>(url, body);
      return response.data.data;
    } catch (error) {
      handleAPIError(error);
    }
  }

  // ── parent ──
  register(payload: ParentRegisterPayload) { return this.post<{ id: string; email: string }>('/api/v1/parents/register', payload); }
  children() { return this.get<ParentChild[]>(`${this.base}/children`); }
  linkChild(code: string) { return this.post<ParentChild>(`${this.base}/children/link`, { code }); }
  async unlinkChild(childId: string): Promise<void> {
    try { await apiClient.delete(`${this.base}/children/${childId}`); } catch (error) { handleAPIError(error); }
  }
  home(childId: string, period: ParentPeriod) { return this.get<ParentHome>(`${this.base}/children/${childId}/home`, { period }); }
  progress(childId: string) { return this.get<ParentProgress>(`${this.base}/children/${childId}/progress`); }
  assessments(childId: string) { return this.get<ParentAssessments>(`${this.base}/children/${childId}/assessments`); }
  activity(childId: string, period: ParentPeriod) { return this.get<ParentActivity>(`${this.base}/children/${childId}/activity`, { period }); }
  insights(childId: string) { return this.get<{ child: ParentChild; items: ParentInsight[] }>(`${this.base}/children/${childId}/insights`); }
  payments() { return this.get<ParentChildPayments[]>(`${this.base}/payments`); }
  approveRequest(requestId: string) {
    return this.post<{ request: PlanRequest; checkout_options: Record<string, any> }>(`${this.base}/plan-requests/${requestId}/approve`);
  }
  declineRequest(requestId: string, reason?: string) { return this.post<PlanRequest>(`${this.base}/plan-requests/${requestId}/decline`, { reason }); }

  // ── child ──
  linkStatus() { return this.get<ParentLinkStatus>('/api/v1/parent-link/status'); }
  newLinkCode() { return this.post<{ code: string; expires_at: string }>('/api/v1/parent-link/code'); }
  askParentForPlan(planId: string, note?: string) { return this.post<PlanRequest>('/api/v1/parent-link/plan-request', { plan_id: planId, note }); }

  /** Silent learning-time tick. Never throws: time tracking must not disturb a lesson. */
  async heartbeat(): Promise<void> {
    try { await apiClient.post('/api/v1/learning-time/heartbeat', {}); } catch { /* ignored on purpose */ }
  }
}

export const parentService = new ParentService();

/** True for a parent account (role name from the token; case differs between environments). */
export const isParentUser = (user: { roles?: string[]; role?: string } | null | undefined): boolean =>
  (user?.roles ?? (user?.role ? [user.role] : [])).some(r => String(r).toLowerCase() === 'parent');
